import {
    CONSENT_CHANGED_EVENT,
    CookieConsent,
    CookieConsentChangedDetail,
    hasAnalyticsConsent,
    readCookieConsent
} from './consent';

type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;
type ConsentValue = 'granted' | 'denied';

declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: (...args: unknown[]) => void;
        japonicsConsentModeDefaultsInitialized?: boolean;
        japonicsAnalytics?: {
            track: (eventName: string, params?: AnalyticsParams) => void;
            consentGranted: () => boolean;
        };
    }
}

const analyticsCookieNames = ['_ga', '_gid', '_gat', '_gcl_au'];
const analyticsCookiePrefixes = ['_ga_', '_gac_'];

let analyticsConsentGranted = false;
let consentDefaultsInitialized = false;
let configuredMeasurementId: string | null = null;
let scriptLoadingMeasurementId: string | null = null;
let pageViewSent = false;
let bookingOpenSent = false;

function getMeasurementId(): string {
    return document.body.getAttribute('data-ga4-measurement-id')?.trim() ?? '';
}

function getCulture(): string {
    return document.body.getAttribute('data-culture') || document.documentElement.lang || 'en';
}

function cleanParams(params: AnalyticsParams = {}): AnalyticsParams {
    const result: AnalyticsParams = {
        page_path: `${window.location.pathname}${window.location.search}`,
        page_title: document.title,
        language: getCulture()
    };

    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        result[key] = value;
    });

    return result;
}

function ensureGtag(): (...args: unknown[]) => void {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || ((...args: unknown[]) => {
        window.dataLayer?.push(args);
    });

    return window.gtag;
}

function consentState(consent: Pick<CookieConsent, 'analytics' | 'marketing'>): Record<string, ConsentValue> {
    const analyticsStorage: ConsentValue = consent.analytics ? 'granted' : 'denied';
    const advertisingStorage: ConsentValue = consent.marketing ? 'granted' : 'denied';

    return {
        ad_storage: advertisingStorage,
        ad_user_data: advertisingStorage,
        ad_personalization: advertisingStorage,
        analytics_storage: analyticsStorage
    };
}

function deniedConsentState(): Record<string, ConsentValue> {
    return consentState({ analytics: false, marketing: false });
}

function initializeConsentDefaults(): void {
    if (consentDefaultsInitialized) return;
    if (window.japonicsConsentModeDefaultsInitialized) {
        consentDefaultsInitialized = true;
        return;
    }

    ensureGtag()('consent', 'default', {
        ...deniedConsentState(),
        wait_for_update: 500
    });
    consentDefaultsInitialized = true;
}

function updateGoogleConsent(consent: Pick<CookieConsent, 'analytics' | 'marketing'>): void {
    initializeConsentDefaults();
    ensureGtag()('consent', 'update', consentState(consent));
}

function setAnalyticsDisabled(measurementId: string, disabled: boolean): void {
    if (!measurementId) return;

    (window as unknown as Record<string, boolean>)[`ga-disable-${measurementId}`] = disabled;
}

function cookieDomainCandidates(): Array<string | null> {
    const host = window.location.hostname;
    const domains: Array<string | null> = [null];

    if (!host || host === 'localhost' || /^[\d.]+$/.test(host)) {
        return domains;
    }

    domains.push(host, `.${host}`);
    const parts = host.split('.');
    if (parts.length > 2) {
        domains.push(`.${parts.slice(-2).join('.')}`);
    }

    return Array.from(new Set(domains));
}

function removeGoogleAnalyticsCookies(measurementId: string): void {
    const measurementCookie = measurementId.startsWith('G-') ? `_ga_${measurementId.slice(2)}` : null;
    const names = document.cookie
        .split(';')
        .map(cookie => cookie.split('=')[0]?.trim())
        .filter((name): name is string => Boolean(name))
        .filter(name =>
            analyticsCookieNames.includes(name)
            || analyticsCookiePrefixes.some(prefix => name.startsWith(prefix))
            || name === measurementCookie);

    if (!names.length) return;

    const expires = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
    const maxAge = 'Max-Age=0';
    const path = 'Path=/';
    const sameSite = 'SameSite=Lax';

    names.forEach(name => {
        cookieDomainCandidates().forEach(domain => {
            const domainPart = domain ? `; Domain=${domain}` : '';
            document.cookie = `${name}=; ${expires}; ${maxAge}; ${path}; ${sameSite}${domainPart}`;
        });
    });
}

function loadGtagScript(measurementId: string): void {
    if (document.querySelector<HTMLScriptElement>(`script[data-gtag="${measurementId}"]`)) return;
    if (scriptLoadingMeasurementId === measurementId) return;

    scriptLoadingMeasurementId = measurementId;
    const script = document.createElement('script');
    script.id = 'ga4-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    script.dataset.gtag = measurementId;
    script.addEventListener('load', () => {
        scriptLoadingMeasurementId = null;
    }, { once: true });
    script.addEventListener('error', () => {
        scriptLoadingMeasurementId = null;
    }, { once: true });
    document.head.appendChild(script);
}

function sendPageView(): void {
    if (pageViewSent || !window.gtag) return;

    window.gtag('event', 'page_view', cleanParams({
        page_location: window.location.href
    }));
    pageViewSent = true;

    if (!bookingOpenSent && window.location.pathname === '/contact/booking') {
        window.gtag('event', 'booking_open', cleanParams({
            source: 'page_view'
        }));
        window.gtag('event', 'calendly_open', cleanParams({
            source: 'page_view'
        }));
        bookingOpenSent = true;
    }
}

function enableAnalytics(): void {
    const measurementId = getMeasurementId();
    const consent = readCookieConsent();
    initializeConsentDefaults();

    if (!measurementId || consent?.analytics !== true) {
        return;
    }

    analyticsConsentGranted = true;
    setAnalyticsDisabled(measurementId, false);
    updateGoogleConsent(consent);
    loadGtagScript(measurementId);

    const gtag = ensureGtag();
    if (configuredMeasurementId !== measurementId) {
        gtag('js', new Date());
        gtag('config', measurementId, {
            send_page_view: false,
            anonymize_ip: true,
            allow_google_signals: false,
            allow_ad_personalization_signals: false
        });
        configuredMeasurementId = measurementId;
    }

    sendPageView();
}

function disableAnalytics(consent: Pick<CookieConsent, 'analytics' | 'marketing'> = { analytics: false, marketing: false }): void {
    const measurementId = getMeasurementId();

    analyticsConsentGranted = false;
    initializeConsentDefaults();

    if (measurementId) {
        setAnalyticsDisabled(measurementId, true);
        removeGoogleAnalyticsCookies(measurementId);
    }

    updateGoogleConsent(consent);
}

export function trackAnalyticsEvent(eventName: string, params: AnalyticsParams = {}): void {
    if (!analyticsConsentGranted && !hasAnalyticsConsent()) return;

    enableAnalytics();
    if (!analyticsConsentGranted || !window.gtag) return;

    window.gtag('event', eventName, cleanParams(params));
}

function classifyInternalPath(pathname: string): string | null {
    if (pathname === '/contact/booking') return 'booking';
    if (pathname === '/contact') return 'contact';
    if (pathname.startsWith('/services')) return 'service';
    if (pathname.startsWith('/platforms')) return 'platform';
    if (pathname.startsWith('/about/philosophy')) return 'philosophy';
    if (pathname.startsWith('/about/culture')) return 'culture';
    if (pathname.startsWith('/about')) return 'about';
    if (pathname.startsWith('/our-crafts')) return 'portfolio';
    if (pathname.startsWith('/resources')) return 'resources';
    if (pathname.startsWith('/blog')) return 'blog';
    if (pathname.startsWith('/career')) return 'career';
    return null;
}

function slugFromPath(pathname: string, prefix: string): string {
    return pathname.slice(prefix.length).split('/').filter(Boolean)[0] || 'index';
}

function initClickTracking(): void {
    document.addEventListener('click', event => {
        const anchor = (event.target as Element | null)?.closest<HTMLAnchorElement>('a[href]');
        if (!anchor) return;

        const href = anchor.getAttribute('href');
        if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

        const url = new URL(anchor.href, window.location.origin);

        if (url.origin !== window.location.origin) {
            trackAnalyticsEvent('outbound_click', {
                link_url: url.href,
                link_domain: url.hostname
            });
            return;
        }

        const category = classifyInternalPath(url.pathname);
        if (!category) return;

        if (url.pathname === '/contact/booking') {
            trackAnalyticsEvent('booking_open', {
                link_text: anchor.textContent?.trim().slice(0, 80) || 'booking'
            });
            trackAnalyticsEvent('calendly_open', {
                link_text: anchor.textContent?.trim().slice(0, 80) || 'booking'
            });
        }

        const service = url.searchParams.get('service');
        if (url.pathname === '/contact' && service) {
            const eventName = window.location.pathname.startsWith('/platforms')
                ? 'platform_interest_click'
                : 'service_interest_click';
            trackAnalyticsEvent(eventName, {
                service
            });
        }

        const platform = url.searchParams.get('platform');
        if (url.pathname === '/contact' && platform) {
            trackAnalyticsEvent('platform_interest_click', {
                platform
            });
        }

        trackAnalyticsEvent('navigation_click', {
            destination_category: category,
            destination_path: url.pathname,
            link_text: anchor.textContent?.trim().slice(0, 80) || category
        });
    });
}

function initBlogCompletionTracking(): void {
    const post = document.getElementById('blogPostPage');
    if (!post) return;

    let sent = false;
    const update = (): void => {
        if (sent) return;

        const total = document.documentElement.scrollHeight - window.innerHeight;
        const ratio = total > 0 ? window.scrollY / total : 0;
        if (ratio < 0.8) return;

        sent = true;
        trackAnalyticsEvent('blog_article_complete', {
            article_slug: post.dataset.slug || slugFromPath(window.location.pathname, '/blog/')
        });
    };

    window.addEventListener('scroll', update, { passive: true });
    update();
}

function exposeGlobalApi(): void {
    window.japonicsAnalytics = {
        track: trackAnalyticsEvent,
        consentGranted: hasAnalyticsConsent
    };
}

export function initAnalytics(): void {
    exposeGlobalApi();
    readCookieConsent();

    if (hasAnalyticsConsent()) {
        enableAnalytics();
    } else {
        disableAnalytics();
    }

    initClickTracking();
    initBlogCompletionTracking();

    window.addEventListener(CONSENT_CHANGED_EVENT, event => {
        const detail = (event as CustomEvent<CookieConsentChangedDetail>).detail;

        if (detail.consent.analytics) {
            enableAnalytics();
            trackAnalyticsEvent(detail.source === 'accept_all'
                ? 'cookie_accept_all'
                : 'cookie_preferences_save', {
                analytics: detail.consent.analytics,
                personalization: detail.consent.personalization,
                marketing: detail.consent.marketing
            });
        } else {
            disableAnalytics(detail.consent);
        }
    });
}
